import { action, makeObservable, observable } from 'mobx';
import { ColumnFiltersMap, ColumnSort, ColumnSortModel, FilterValue } from '@Pages/Home/Components';

export type TableFilterState = {
    columnSort: ColumnSortModel | null;
    columnFilters: ColumnFiltersMap;
};

export class FilterTableStore {
    private _tabId?: string;
    @observable public columnSort: ColumnSortModel | null = null;
    @observable public columnFilters: ColumnFiltersMap = new Map();

    constructor(tabId?: string) {
        this._tabId = tabId;
        makeObservable(this);
        this._getDefaultFilters();
    }

    private _getDefaultFilters() {
        if (!this._tabId) return;

        const config = localStorage.getItem(`tableFilters_${this._tabId}`);
        const tableFilters = config ? JSON.parse(config) as TableFilterState : null;

        if (!tableFilters) return;

        this.columnFilters = new Map(tableFilters.columnFilters);
        this.columnSort = tableFilters.columnSort;
    }

    @action.bound
    public setColumnSort(key: string, sortType: ColumnSort) {
        if (this.columnSort?.key === key && this.columnSort.sortType === sortType) {
            this.columnSort = null;
        } else {
            this.columnSort = { key, sortType };
        }
        this._saveTableFilterState();
    }

    @action.bound
    public setColumnFilters(key: string, filter: FilterValue) {
        const columnFilters = this.columnFilters.get(key);

        if (columnFilters) {
            const index = columnFilters.findIndex(f => f === filter);
            if (index === -1) {
                columnFilters.push(filter);
                this._saveTableFilterState();
                return;
            }

            columnFilters.splice(index, 1);

            if (!columnFilters.length) {
                this.columnFilters.delete(key);
            }

            this._saveTableFilterState();
            return;
        }

        this.columnFilters.set(key, [filter]);
        this._saveTableFilterState();
    }

    private _saveTableFilterState() {
        if (!this._tabId) return;

        const tableFilters = {
            columnSort: this.columnSort,
            columnFilters: this.columnFilters
        };

        localStorage.setItem(`tableFilters_${this._tabId}`, JSON.stringify(tableFilters));
    }

    @action.bound
    public clearFilters(){
        this.columnSort = null;
        this.columnFilters = new Map();

        if (!this._tabId) return;
        localStorage.removeItem(`tableFilters_${this._tabId}`);
    }

    @action.bound
    public getIsFilterActive(key: string) {
        return this.columnSort?.key === key || !!this.columnFilters.get(key);
    }
}